import { DataEHoraAmigavel } from '@/shared/utils/datas';
import { GetNotificacoes, SetNotificacao } from '@/modules/system/notificacoes/notificacoes.service';
import {
  notificacaoDetalhesExibicao,
  notificacaoTituloExibicao,
} from '@/modules/system/notificacoes/notificacao-display.utils';
import { Link } from 'react-router-dom';
import { Links } from '@/shared/constants/navegacao';
import { NotificacaoDto } from '@/api.types';
import { RiNotificationLine } from 'react-icons/ri';
import { useEffect, useState, useRef } from 'react';

type NotificationBellVariant = 'navbar' | 'header';

interface NotificationBellProps {
  variant?: NotificationBellVariant;
}

export default function NotificationBell({ variant = 'navbar' }: NotificationBellProps) {
  const [notificacoes, setNotificacoes] = useState<NotificacaoDto[]>([]);
  const [notificacoesCount, setNotificacoesCount] = useState(0);
  const [showNotificacoes, setShowNotificacoes] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Carregar notificações periodicamente
  const carregarNotificacoes = async () => {
    try {
      const resultado = await GetNotificacoes(true);
      if (resultado && resultado.objects) {
        setNotificacoes(resultado.objects.slice(0, 5)); // Mostrar apenas as 5 mais recentes
        setNotificacoesCount(resultado.count || 0);
      }
    } catch (error) {
      // console.error('Erro ao carregar notificações:', error);
    }
  };

  useEffect(() => {
    carregarNotificacoes();

    // Configurar polling a cada 30 segundos
    intervalRef.current = setInterval(() => {
      carregarNotificacoes();
    }, 30000);

    // Limpar intervalo ao desmontar
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showNotificacoes && !target.closest('#dropdownNotificacoes') && !target.closest('.dropdown-menu')) {
        setShowNotificacoes(false);
      }
    };

    if (showNotificacoes) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNotificacoes]);

  const handleMarcarComoLida = async (id: string) => {
    try {
      await SetNotificacao(id);
      // Atualizar lista após marcar como lida
      carregarNotificacoes();
    } catch (error) {
      console.error('Erro ao marcar notificação como lida:', error);
    }
  };

  const getLinkParaNotificacao = (notificacao: NotificacaoDto): string | null => {
    if (!notificacao.idregistro) {
      return null;
    }

    switch (notificacao.tipo) {
      case 'ACAO':
        return Links.Acoes_Editar + '/' + notificacao.idregistro;
      case 'LOTACAO':
        return Links.Vinculos_Editar + '/' + notificacao.idregistro;
      case 'PESSOA':
        return Links.Pessoa_Editar + '/' + notificacao.idregistro;
      default:
        // Fallback para compatibilidade com notificações antigas sem tipo
        return Links.Acoes_Editar + '/' + notificacao.idregistro;
    }
  };

  const isHeader = variant === 'header';

  return (
    <div className={isHeader ? 'app-header__notifications' : undefined}>
      <Link
        className={
          isHeader
            ? 'app-header__notif-btn position-relative'
            : 'nav-link position-relative'
        }
        to="#"
        id="dropdownNotificacoes"
        data-bs-toggle="dropdown"
        aria-haspopup="true"
        aria-expanded={showNotificacoes}
        aria-label="Notificações"
        title="Notificações"
        onClick={(e) => {
          e.preventDefault();
          setShowNotificacoes(!showNotificacoes);
        }}
      >
        <RiNotificationLine size={isHeader ? 18 : 14} color={isHeader ? undefined : '#fff'} />
        {notificacoesCount > 0 && (
          <span
            className={
              isHeader
                ? 'position-absolute translate-middle badge rounded-pill app-header__notif-badge'
                : 'position-absolute top-5 start-100 translate-middle badge rounded-pill'
            }
            style={isHeader ? undefined : { fontSize: '0.7rem', minWidth: '18px' }}
          >
            {notificacoesCount > 99 ? '99+' : notificacoesCount}
          </span>
        )}
      </Link>
      <div
        className={`dropdown-menu dropdown-menu-end ${showNotificacoes ? 'show' : ''}`}
        aria-labelledby="dropdownNotificacoes"
        style={{
          minWidth: '350px',
          maxWidth: '400px',
          maxHeight: '500px',
          overflowY: 'auto',
        }}
      >
        <div className="dropdown-header d-flex justify-content-between align-items-center">
          <strong>Notificações</strong>
          <Link
            to={Links.Notificacoes_index}
            className="text-decoration-none small"
            onClick={() => setShowNotificacoes(false)}
          >
            Ver todas
          </Link>
        </div>
        <div className="dropdown-divider"></div>
        {notificacoes.length === 0 ? (
          <div className="dropdown-item-text text-muted text-center py-3">
            Nenhuma notificação
          </div>
        ) : (
          notificacoes.map((notificacao, index) => {
            const titulo = notificacaoTituloExibicao(notificacao.titulo);
            const detalhes = notificacaoDetalhesExibicao(notificacao.detalhes);

            return (
            <div key={notificacao.id}>
              <div className="dropdown-item-text">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div className="flex-grow-1">
                    <div className="fw-bold small">{titulo}</div>
                    {detalhes != null && (
                      <div className="text-muted small" style={{ fontSize: '0.85rem' }}>
                        {detalhes}
                      </div>
                    )}
                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                      {DataEHoraAmigavel(notificacao.criadoEm)}
                    </div>
                  </div>
                  {!notificacao.exibidapopup && (
                    <button
                      className="btn btn-sm btn-outline-primary ms-2"
                      title="Marcar como lida"
                      onClick={() => handleMarcarComoLida(notificacao.id)}
                    >
                      ✓
                    </button>
                  )}
                </div>
                {(() => {
                  const link = getLinkParaNotificacao(notificacao);
                  return link ? (
                    <Link
                      to={link}
                      className="btn btn-sm btn-link p-0 text-decoration-none"
                      onClick={() => setShowNotificacoes(false)}
                    >
                      Ver detalhes →
                    </Link>
                  ) : null;
                })()}
              </div>
              {index < notificacoes.length - 1 && <div className="dropdown-divider"></div>}
            </div>
            );
          })
        )}
      </div>
    </div>
  );
}

